/**
 * MemoryManager — the list, add/edit/delete dialogs and the import shared by
 * Personal memory and Brand memory (Figma 09 · Personal memory 01–20, Brand
 * memory 17–19). The pages own the data hooks, the page head and the copy.
 * Every write is optimistic: dialogs close on confirm, and a failure rolls the
 * row back with a toast (see memoryMutations.ts).
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { SPRING_SMOOTH } from "@/lib/motion";
import { Brain, Check, Copy, Pencil, Trash2 } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { cn } from "@/utils/helpers";
import { queue } from "@/components/Toast";
import {
  isPendingMemory,
  type MemoryBase,
  type MemoryImportFile,
  type MemoryImportPreview,
} from "@/app/routes/dashboard/settings/api/memoryMutations";
import type { MemoryExportFormat } from "@/app/routes/dashboard/settings/memoryExport";
import { KitButton } from "../../kit/Button";
import { KitIconButton } from "../../kit/IconButton";
import { KitTextarea } from "../../kit/Textarea";
import { KitConsequenceWarning, KitModal } from "../../kit/Modal";
import { EmptyState, ErrorState } from "../../ViewStates";
import { Card, ConfirmDialog, DialogFooter, QuietNote } from "../parts/SettingsParts";
import { agoLabel } from "../parts/settingsFormat";
import MemoryImportModal from "./MemoryImportModal";
import MemoryExportModal from "./MemoryExportModal";

export type MemoryManagerCopy = {
  /** "Add memory" / "Add to Fatafeat's memory". */
  addTitle: string;
  emptyTitle: string;
  empty: string;
  placeholder: string;
  full: string;
  importDescription: string;
  loadErrorTitle: string;
  loadError: string;
  loadingCaption: string;
};

function MemorySkeleton({ caption }: { caption: string }) {
  return (
    <div className="flex flex-col gap-1" aria-label={caption}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex animate-pulse items-start gap-4 px-4 py-3.5">
          <div className="flex flex-1 flex-col gap-2.5">
            <div className={cn("h-3 bg-co-hover", i === 1 ? "w-1/2" : "w-3/4")} />
            <div className="h-3 w-24 bg-co-hover" />
          </div>
        </div>
      ))}
    </div>
  );
}

type MemoryRowProps<M extends MemoryBase> = {
  memory: M;
  sourceLabel: string;
  canEdit: boolean;
  onEdit: (memory: M) => void;
  onDelete: (memory: M) => void;
  /** Arrived since the list was first shown: tinted for 2s so it can be found (M13). */
  fresh?: boolean;
};

function MemoryRow<M extends MemoryBase>({ memory, sourceLabel, canEdit, onEdit, onDelete, fresh }: MemoryRowProps<M>) {
  const pending = isPendingMemory(memory);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(memory.body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked: the sentence is on screen to select by hand.
    }
  };

  return (
    <div
      data-fresh={fresh || undefined}
      className={cn(
        "co-memory-row flex items-start gap-4 rounded-card px-4 py-3.5 transition-opacity hover:bg-co-hover",
        pending && "opacity-60",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-[14px] leading-[1.5] text-co-text">{memory.body}</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-[6px] border border-co-line2 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-co-muted">
            {sourceLabel}
          </span>
          <span className="text-[11.5px] text-co-faint">{pending ? "Saving…" : agoLabel(memory.updatedAt)}</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <KitIconButton icon={copied ? Check : Copy} aria-label={copied ? "Copied" : "Copy memory"} onClick={() => void copy()} />
        {canEdit ? (
          <>
            <KitIconButton icon={Pencil} aria-label="Edit memory" onClick={() => onEdit(memory)} disabled={pending} />
            <KitIconButton icon={Trash2} aria-label="Delete memory" onClick={() => onDelete(memory)} disabled={pending} />
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function MemoryManager<M extends MemoryBase>({
  memories,
  cap,
  isLoading,
  isError,
  onRetry,
  canEdit,
  bodyMaxLength,
  sourceLabels,
  copy,
  onCreate,
  onUpdate,
  onDelete,
  importPreview,
  onImport,
  exportFormat,
  copyPrompt,
}: {
  memories: M[];
  cap: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canEdit: boolean;
  /** Mirrors the backend's *_BODY_MAX_LENGTH for this kind of memory. */
  bodyMaxLength: number;
  sourceLabels: Record<string, string>;
  copy: MemoryManagerCopy;
  onCreate: (body: string) => void;
  onUpdate: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  importPreview: UseMutationResult<MemoryImportPreview, unknown, MemoryImportFile[]>;
  onImport: (bodies: string[]) => void;
  /** "Copy memory prompt" opens the export modal with this wording. */
  exportFormat?: MemoryExportFormat;
  /** When set, "Copy memory prompt" copies this text instead of opening the export modal. */
  copyPrompt?: string;
}) {
  const [editor, setEditor] = useState<{ id: string | null; original: string } | null>(null);
  const [draft, setDraft] = useState("");
  const [discardAsk, setDiscardAsk] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<M | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const atCap = cap > 0 && memories.length >= cap;

  const copyPromptToClipboard = async () => {
    if (!copyPrompt) return;
    try {
      await navigator.clipboard.writeText(copyPrompt);
      setPromptCopied(true);
      window.setTimeout(() => setPromptCopied(false), 2000);
      queue.add({
        title: "Prompt copied",
        description: "Ask your assistant with it, save its answer as a .md file, then Import it here.",
        variant: "success",
      });
    } catch {
      queue.add({ title: "Could not copy", description: "Your browser blocked the clipboard.", variant: "error" });
    }
  };

  const openEditor = (memory: M | null) => {
    setEditor({ id: memory?.id ?? null, original: memory?.body ?? "" });
    setDraft(memory?.body ?? "");
  };
  const closeEditor = () => {
    setEditor(null);
    setDraft("");
    setDiscardAsk(false);
  };
  // Closing with unsaved text asks first (Personal memory / 08).
  const requestClose = () => {
    if (editor && draft.trim() && draft.trim() !== editor.original.trim()) setDiscardAsk(true);
    else closeEditor();
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !editor) return;
    if (editor.id) onUpdate(editor.id, body);
    else onCreate(body);
    closeEditor();
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <KitButton
          variant="secondary"
          iconLeft={copyPrompt && promptCopied ? Check : undefined}
          onClick={() => (copyPrompt ? void copyPromptToClipboard() : setExportOpen(true))}
          isDisabled={copyPrompt ? false : memories.length === 0}
        >
          {copyPrompt && promptCopied ? "Copied" : "Copy memory prompt"}
        </KitButton>
        {canEdit ? (
          <>
            <KitButton
              variant="secondary"
              onClick={() => {
                importPreview.reset();
                setImportOpen(true);
              }}
              isDisabled={atCap}
            >
              Import
            </KitButton>
            <KitButton onClick={() => openEditor(null)} isDisabled={atCap}>
              Add memory
            </KitButton>
          </>
        ) : null}
        {cap > 0 && !isLoading && !isError ? (
          <span className="ml-2 text-[12.5px] tabular-nums text-co-muted">
            {memories.length} of {cap} memories
          </span>
        ) : null}
      </div>

      {atCap && canEdit ? <QuietNote>{copy.full}</QuietNote> : null}

      {isLoading ? (
        <MemorySkeleton caption={copy.loadingCaption} />
      ) : isError ? (
        <ErrorState title={copy.loadErrorTitle} body={copy.loadError} onRetry={onRetry} minHeight={280} />
      ) : memories.length === 0 ? (
        <EmptyState icon={<Brain aria-hidden />} title={copy.emptyTitle} body={copy.empty} minHeight={280} />
      ) : (
        <MemoryList
          memories={memories}
          render={(memory, fresh) => ({
            memory,
            fresh,
            sourceLabel: sourceLabels[memory.source] ?? memory.source,
            canEdit,
            onEdit: openEditor,
            onDelete: setDeleteTarget,
          })}
        />
      )}

      {editor && !discardAsk ? (
        <KitModal
          isOpen
          onOpenChange={(open) => (open ? undefined : requestClose())}
          title={editor.id ? "Edit memory" : copy.addTitle}
          description={
            editor.id
              ? "The agents use the new wording from your next message."
              : "One preference, as the agents should apply it."
          }
          modalClassName="!w-[min(calc(100vw-2rem),520px)] !max-w-[520px]"
        >
          <form onSubmit={save} className="flex w-full flex-col gap-2">
            <KitTextarea
              label="Memory"
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, bodyMaxLength))}
              placeholder={copy.placeholder}
              maxLength={bodyMaxLength}
              rows={4}
              autoFocus
            />
            <span
              className={cn(
                "self-end text-[11.5px] tabular-nums",
                draft.length >= bodyMaxLength ? "text-co-alert-ink" : "text-co-faint",
              )}
            >
              {draft.length} of {bodyMaxLength} characters
            </span>
            <DialogFooter type="submit" confirmLabel="Save" onCancel={requestClose} disabled={!draft.trim()} />
          </form>
        </KitModal>
      ) : null}

      {discardAsk ? (
        <ConfirmDialog
          title="Discard this memory?"
          description="What you typed is not saved."
          cancelLabel="Keep writing"
          confirmLabel="Discard"
          onClose={() => setDiscardAsk(false)}
          onConfirm={closeEditor}
        />
      ) : null}

      {deleteTarget ? (
        <ConfirmDialog
          title="Delete this memory?"
          description={`“${deleteTarget.body}” The agents stop applying it from the next message.`}
          confirmLabel="Delete"
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            onDelete(deleteTarget.id);
            setDeleteTarget(null);
          }}
        >
          <KitConsequenceWarning />
        </ConfirmDialog>
      ) : null}

      {importOpen ? (
        <MemoryImportModal
          onClose={() => setImportOpen(false)}
          preview={importPreview}
          onSave={onImport}
          maxLength={bodyMaxLength}
          description={copy.importDescription}
        />
      ) : null}

      {exportOpen && exportFormat ? (
        <MemoryExportModal memories={memories} format={exportFormat} onClose={() => setExportOpen(false)} />
      ) : null}
    </Card>
  );
}

/**
 * The list, with Settings M13 and M14: new rows arrive at the top and push the
 * rest down by transform, rising 8px, 20ms apart, tinted for 2s; a deleted row
 * leaves in 160ms and the list closes the gap on smooth. Under reduced motion
 * rows fade (the tint still marks the new ones).
 */
function MemoryList<M extends MemoryBase>({
  memories,
  render,
}: {
  memories: M[];
  render: (memory: M, fresh: boolean) => MemoryRowProps<M>;
}) {
  // The rows on the list when it was first shown are not new; anything after is,
  // until its tint has run (2.6s).
  const [seen, setSeen] = useState<ReadonlySet<string>>(() => new Set(memories.map((m) => m.id)));
  const fresh = memories.filter((m) => !seen.has(m.id)).map((m) => m.id);
  const freshKey = fresh.join(",");
  useEffect(() => {
    if (!freshKey) return;
    const id = window.setTimeout(() => setSeen((prev) => new Set([...prev, ...freshKey.split(",")])), 2600);
    return () => window.clearTimeout(id);
  }, [freshKey]);
  return (
    <div className="-mx-4 flex flex-col">
      <AnimatePresence initial={false}>
        {memories.map((memory) => {
          const index = fresh.indexOf(memory.id);
          return (
            <motion.div
              key={memory.id}
              layout="position"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.16 } }}
              transition={{ ...SPRING_SMOOTH, delay: index > 0 ? index * 0.02 : 0 }}
            >
              <MemoryRow {...render(memory, index >= 0)} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
